import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink } from 'lucide-react';
import { useDesigns } from '@/hooks/use-content';
import type { DesignRow as Design } from '@/types/database';
import SectionEyebrow from './SectionEyebrow';

const DesignCard = memo(({ design, onClick }: { design: Design; onClick: () => void }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-square overflow-hidden rounded-xl bg-muted text-left hud-frame"
      aria-label={t('designs.viewAria', { title: design.title })}
    >
      {isLoading && <Skeleton className="absolute inset-0 w-full h-full rounded-none" />}
      <img
        src={design.image_url}
        alt={design.title}
        loading="lazy"
        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setIsLoading(false)}
        onError={(e) => {
          setIsLoading(false);
          e.currentTarget.src = "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=400&fit=crop";
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
        <span className="text-sm font-semibold text-foreground truncate">{design.title}</span>
        <span className="text-xs text-primary/90 uppercase tracking-wide">{design.category}</span>
      </div>
    </button>
  );
});
DesignCard.displayName = 'DesignCard';

const DesignLightbox = ({
  design,
  isOpen,
  onClose,
}: {
  design: Design | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  if (!design) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl w-full max-h-screen overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{design.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg overflow-hidden bg-muted flex items-center justify-center">
            <img
              src={design.image_url}
              alt={design.title}
              className="w-full h-auto max-h-[65vh] object-contain"
            />
          </div>

          <div className="flex items-center gap-3">
            <Badge className="capitalize">{design.category}</Badge>
            {design.link && (
              <a
                href={design.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-accent transition-colors"
              >
                {t('designs.viewMore')} <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>

          {design.description && (
            <p className="text-muted-foreground text-sm leading-relaxed">{design.description}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Designs = () => {
  const { t } = useTranslation();
  const { data: designsData, isLoading } = useDesigns();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedDesign, setSelectedDesign] = useState<Design | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const categories = designsData ? Array.from(new Set(designsData.map((d) => d.category))) : [];
  const visibleDesigns = !designsData
    ? []
    : activeCategory === 'all'
    ? designsData
    : designsData.filter((d) => d.category === activeCategory);

  const handleClick = (design: Design) => {
    setSelectedDesign(design);
    setIsLightboxOpen(true);
  };

  return (
    <section id="designs" className="py-14 sm:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="05" label={t('designs.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('designs.title')}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t('designs.description')}</p>
        </div>

        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCategory === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('designs.all')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${
                  activeCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {isLoading
            ? Array.from({ length: 8 }).map((_, idx) => <Skeleton key={idx} className="aspect-square rounded-xl" />)
            : visibleDesigns.map((design) => (
                <DesignCard key={design.id} design={design} onClick={() => handleClick(design)} />
              ))}
        </div>

        {!isLoading && visibleDesigns.length === 0 && (
          <p className="text-center text-muted-foreground py-8">{t('designs.noDesigns')}</p>
        )}
      </div>

      <DesignLightbox design={selectedDesign} isOpen={isLightboxOpen} onClose={() => setIsLightboxOpen(false)} />
    </section>
  );
};

export default Designs;
